import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbDateParserFormatter } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { phoneNumberValidator } from 'src/app/directives/phone-number/phone-number.directive';
import { socialSecurityNumberValidator } from 'src/app/directives/ssn/ssn.directive';
import { handleErrorMessage } from 'src/app/helpers/error-handler.helper';
import { DataLayerService } from 'src/app/services/analytics/data-layer.service';
import {
  DropdownMenus,
  EmploymentStatusType,
  PayType,
} from 'src/app/services/dropdowns/dropdowns.model';
import { DropdownsService } from 'src/app/services/dropdowns/dropdowns.service';
import { ErrorMessageService } from 'src/app/services/error-message/error-message.service';
import { FinalFurnitureLoanApplication } from 'src/app/services/furniture-credit/furniture-credit.model';
import { FurnitureCreditService } from 'src/app/services/furniture-credit/furniture-credit.service';
import { NavigationService } from 'src/app/services/navigation/navigation.service';

@Component({
  selector: 'app-furniture-applicant-employer',
  templateUrl: './furniture-applicant-employer.component.html',
  styleUrls: ['./furniture-applicant-employer.component.scss'],
})
export class FurnitureApplicantEmployerComponent implements OnInit {
  employerFormGroup: FormGroup;
  loanApplication: FinalFurnitureLoanApplication | null = null;
  dropdownMenus: DropdownMenus | null = null;
  isSaving = false;
  isDirtyAndMissingRequiredFields = false;
  unemployed = false;

  private subscriptions: Array<Subscription> = [];

  constructor(
    private formBuilder: FormBuilder,
    private navService: NavigationService,
    private creditService: FurnitureCreditService,
    private dropdownsService: DropdownsService,
    private router: Router,
    private errorMessageService: ErrorMessageService,
    private ngbDateParserService: NgbDateParserFormatter,
    private titleService: Title,
    private dataLayerService: DataLayerService
  ) {
    this.titleService.setTitle('Sims Furniture | Finish & Submit');
    this.navService.setBackNav('Applicant Info', '/furniture/applicant');

    this.employerFormGroup = this.formBuilder.group({
      EmployerName: ['', Validators.required],
      Position: ['', Validators.required],
      Years: ['', [Validators.required, Validators.min(0)]],
      Months: [
        '',
        [Validators.required, Validators.min(0), Validators.max(12)],
      ],
      PhoneNumber: ['', [Validators.required, phoneNumberValidator]],
      PhoneNumberType: [{ value: '', disabled: true }],
      EmailAddress: [{ value: '', disabled: true }],
      PayTypeCD: ['', Validators.required],
      PayPeriod: ['', Validators.required],
      HoursPerWeek: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      HourlyRate: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      TakeHomePay: ['', [Validators.required, Validators.min(0)]],
      AnnualSalary: ['', [Validators.required, Validators.min(0)]],
      NetIncomePPP: [
        { value: '', disabled: true },
        [Validators.required, Validators.min(0)],
      ],
      OtherIncomes: [[]],
      MonthlyAdditionalIncome: ['', Validators.min(0)],
      DateOfBirthYear: ['', Validators.required],
      DateOfBirthMonth: ['', Validators.required],
      DateOfBirthDay: ['', Validators.required],
      DateOfBirth: [''],
      SSN: ['', [Validators.required, socialSecurityNumberValidator]],
      CreditReportAuthorization: [
        { value: '', disabled: true },
        Validators.requiredTrue,
      ],
      // {DisclaimerForOhioApplicants} is no longer needed. Setting as disabled to hide from UI.
      DisclaimerForOhioApplicants: [{ value: '', disabled: true }],
    });
  }

  ngOnInit(): void {
    this.handleSubscriptions();

    // On refresh of the page it will redirect to start of application.
    if (!this.loanApplication) {
      this.router.navigateByUrl('/furniture/applicant', { replaceUrl: true });
    }

    if (!this.dropdownMenus) {
      this.dropdownsService.getDropdowns();
    }
  }

  private handleSubscriptions() {
    this.subscriptions.push(this.finalFurnitureLoanApplication$Subscription());

    this.subscriptions.push(
      this.dropdownsService.dropdownMenus.subscribe(
        (data: DropdownMenus | null) => {
          this.dropdownMenus = null;

          if (data) {
            this.dropdownMenus = { ...data };
          }
        }
      )
    );

    this.subscriptions.push(
      this.employerFormGroup.statusChanges.subscribe((status: string) => {
        if (status && status === 'VALID') {
          this.isDirtyAndMissingRequiredFields = false;
        }
      })
    );
  }

  finalFurnitureLoanApplication$Subscription(): Subscription {
    return this.creditService.finalFurnitureLoanApplication.subscribe(
      (data: FinalFurnitureLoanApplication | null) => {
        if (data) {
          this.loanApplication = { ...data };

          if (data.LoanApplication) {
            this.setFormValues(data.LoanApplication);
          }

          // `Applicant` data only fills the SSN and DOB fields on the employer form.
          if (data.Applicant) {
            this.employerFormGroup.controls['SSN'].setValue(data.Applicant.SSN);

            if (data.Applicant.DateOfBirth) {
              const dob = data.Applicant.DateOfBirth.split('-');

              this.employerFormGroup.controls['DateOfBirth'].setValue(
                data.Applicant.DateOfBirth
              );
              this.employerFormGroup.controls['DateOfBirthYear'].setValue(
                dob[0]
              );
              this.employerFormGroup.controls['DateOfBirthMonth'].setValue(
                dob[1]
              );
              this.employerFormGroup.controls['DateOfBirthDay'].setValue(
                dob[2]
              );

              // Convert string to {NgbDateStruct} and set control.
              // const dob = this.ngbDateParserService.parse(
              //   data.Applicant.DateOfBirth
              // );

              // this.employerFormGroup.controls['DateOfBirth'].setValue(dob);
            }
          }

          if (data.ApplicantEmployment) {
            this.unemployed =
              data.ApplicantEmployment.EmploymentStatus ===
                EmploymentStatusType.unemployed ||
              data.ApplicantEmployment.EmploymentStatus ===
                EmploymentStatusType.retired;

            if (this.unemployed) {
              this.setFormControlsForEmploymentStatus(
                this.unemployed,
                data.ApplicantEmployment.EmploymentStatus ===
                  EmploymentStatusType.retired
              );

              this.employerFormGroup.controls['TakeHomePay'].setValue(
                data.ApplicantEmployment.TakeHomePay || ''
              );
            } else {
              const payType = data.ApplicantEmployment?.PayTypeCD ?? '';

              this.setFormControlsForPayType(payType);
              this.setFormValues(data.ApplicantEmployment);
            }

            // Hide the other income controls if the applicant is retired.
            if (
              data.ApplicantEmployment.EmploymentStatus ===
              EmploymentStatusType.retired
            ) {
              this.onClearOtherIncomesClick();
              this.employerFormGroup.controls['OtherIncomes'].disable();
              this.employerFormGroup.controls[
                'MonthlyAdditionalIncome'
              ].disable();
            } else {
              if (data.ApplicantAdditionalIncome) {
                this.setFormValues(data.ApplicantAdditionalIncome);
                this.onOtherIncomeChange(
                  data.ApplicantAdditionalIncome.OtherIncomes ?? []
                );
              }
            }
          }
        }
      }
    );
  }

  /**
   * The function will crawl through the provided object keys and set the form values.
   *
   * @param data Any property from the {FinalFurnitureLoanApplication} object.
   */
  setFormValues(data: any) {
    // Set form values
    Object.keys(data).forEach((key) => {
      // Check if key exists on current form.
      if (this.employerFormGroup.controls[key]) {
        this.employerFormGroup.controls[key].setValue(data?.[key]);
      }
    });
  }

  /**
   * Disable {FormControl}s if applicant is unemployed or retired.
   *
   * @param disable Defaults to `false`. Will disable the control if `true` otherwise it will be enabled.
   * @param isRetired Defaults to `false`. If retired, form will keep the control `TakeHomePay` enabled.
   */
  setFormControlsForEmploymentStatus(disable = false, isRetired = false) {
    const deactivateOnUnemployed = [
      'EmployerName',
      'Position',
      'Years',
      'Months',
      'PhoneNumber',
      'TakeHomePay',
      'PayTypeCD',
      'PayPeriod',
      'AnnualSalary',
    ];

    Object.keys(this.employerFormGroup.controls).forEach((key) => {
      if (disable) {
        if (isRetired) {
          if (deactivateOnUnemployed.includes(key) && key !== 'TakeHomePay') {
            this.employerFormGroup.controls[key].disable();
          }
        } else if (deactivateOnUnemployed.includes(key)) {
          this.employerFormGroup.controls[key].disable();
        }
      } else {
        if (deactivateOnUnemployed.includes(key)) {
          this.employerFormGroup.controls[key].enable();
        }
      }
    });
  }

  /**
   * Disable {FormControl}s based on applicant's pay type.
   *
   * @param payType Defaults to empty string.
   */
  setFormControlsForPayType(payType = '') {
    const disableKeysForHourly = ['AnnualSalary'];
    const disableKeysForOther = ['HoursPerWeek', 'HourlyRate'];

    // Enable in case they were disabled previously by a retired or unemployed applicant.
    this.employerFormGroup.controls['AnnualSalary'].enable();

    Object.keys(this.employerFormGroup.controls).forEach((key) => {
      if (payType === PayType.hourly) {
        // Disable keys for hourly pay type.
        if (disableKeysForHourly.includes(key)) {
          this.employerFormGroup.controls[key].disable();
        } else if (disableKeysForOther.includes(key)) {
          this.employerFormGroup.controls[key].enable();
        }
      } else {
        // Enable keys for other pay types.
        if (disableKeysForHourly.includes(key)) {
          this.employerFormGroup.controls[key].enable();
        } else if (disableKeysForOther.includes(key)) {
          this.employerFormGroup.controls[key].disable();
        }
      }
    });
  }

  /**
   * Submit application.
   */
  async onNext(): Promise<void> {
    const emplFG = this.employerFormGroup;

    this.isDirtyAndMissingRequiredFields = false;

    if (emplFG.invalid) {
      emplFG.markAllAsTouched();
      this.isDirtyAndMissingRequiredFields = true;
      return;
    }

    this.dataLayerService.logEvent(
      'button_clicked',
      'credit_application',
      'click',
      'Submit Credit Application'
    );

    const loan = this.creditService.setApplicantEmployer({
      ...emplFG.value,
      DateOfBirth: `${emplFG.value.DateOfBirthYear}-${emplFG.value.DateOfBirthMonth}-${emplFG.value.DateOfBirthDay}`,
    });

    await this.saveCreditApplication(loan);
  }

  onExitApplication() {
    this.dataLayerService.logEvent(
      'button_clicked',
      'credit_application',
      'click',
      'Exit Credit Application'
    );

    this.creditService.clearApplication();
    this.router.navigateByUrl('/furniture/applicant', { replaceUrl: true });
  }

  onModalClose() {
    this.onExitApplication();
  }

  private async saveCreditApplication(
    loan: FinalFurnitureLoanApplication
  ): Promise<void> {
    try {
      this.isSaving = true;

      const response = await this.creditService.postCreditApplication(
        loan,
        'loan/final'
      );

      if (response) {
        // Set the selected branch for the confirmation page.
        const branch = response.Branch ?? null;

        this.creditService.clearApplication();

        this.router.navigateByUrl('/furniture/applicant/confirmation', {
          state: {
            branch,
          },
        });
      }

      return;
    } catch (error) {
      this.errorMessageService.addToErrorMessages(handleErrorMessage(error));
    } finally {
      this.isSaving = false;
    }
  }

  onClearOtherIncomesClick() {
    const otherIncomes = this.employerFormGroup.controls['OtherIncomes'];
    const monthlyAdditionalIncome =
      this.employerFormGroup.controls['MonthlyAdditionalIncome'];

    otherIncomes.setValue([]);
    monthlyAdditionalIncome.setValue('');
    monthlyAdditionalIncome.clearValidators();
    monthlyAdditionalIncome.updateValueAndValidity();
  }

  onOtherIncomeChange(value: Array<string>) {
    const control = this.employerFormGroup.controls['MonthlyAdditionalIncome'];

    if (value.length === 0) {
      control.clearValidators();
    } else {
      control.addValidators([Validators.required, Validators.min(0)]);
    }

    control.updateValueAndValidity();
  }

  onPayTypeChange(value: string) {
    // Get form data and update the loan object to save progress.
    this.creditService.setApplicantEmployer({
      ...this.employerFormGroup.value,
      // DateOfBirth: this.ngbDateParserService.format(
      //   this.employerFormGroup.value.DateOfBirth
      // ),
    });

    this.creditService.clearOnPayTypeChange(value);

    this.setFormControlsForPayType(value);
  }
}
